import React, { useState, useEffect } from "react";
import EmailImageUpload from "../EmailImageUpload";
import { useEmailSections } from "../../Context/EmailSectionContext";
import { validateImage } from "../../../utils/imageHelper";
import StyleControls from "./StyleControls";

const Banner = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  // Use context in edit mode, props in preview mode
  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  // Extract data with fallbacks
  const {
    title = "",
    description = "",
    image = null,
    link = "",
    height = "300px",
    overlay = false,
    overlay_color = "#000000",
    overlay_opacity = 0.5,
    text_color = "#333333",
    text_position = "below",
    component_name = "Banner",
  } = sectionData;

  // Local state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [bannerTitle, setBannerTitle] = useState(title);
  const [bannerDescription, setBannerDescription] = useState(description);
  const [bannerImage, setBannerImage] = useState(validateImage(image));
  const [bannerImageUrl, setBannerImageUrl] = useState(link || "");

  const [bannerHeight, setBannerHeight] = useState(height);
  const [hasOverlay, setHasOverlay] = useState(overlay);
  const [overlayColor, setOverlayColor] = useState(overlay_color);
  const [overlayOpacity, setOverlayOpacity] = useState(overlay_opacity);
  const [textColor, setTextColor] = useState(text_color);
  const [textPosition, setTextPosition] = useState(text_position);

  useEffect(() => {
    if (bannerImage instanceof File || bannerImage instanceof Blob) {
      const url = URL.createObjectURL(bannerImage);
      setBannerImageUrl(url);

      return () => URL.revokeObjectURL(url); // Clean up
    }

    if (typeof bannerImage === "string") {
      setBannerImageUrl(bannerImage);
    }

    if (!bannerImage) {
      setBannerImageUrl(null);
    }
  }, [bannerImage]);

  // Sync local state with context/props - only when section id changes
  useEffect(() => {
    setBannerTitle(title);
    setBannerDescription(description);
    setBannerImage(validateImage(image));
    setBannerHeight(height);
    setHasOverlay(overlay);
    setOverlayColor(overlay_color);
    setOverlayOpacity(overlay_opacity);
    setTextColor(text_color);
    setTextPosition(text_position);
  }, [id]); // Only sync when section id changes, not on every prop change
  
  // Merge section-specific styles with parent styles
  const sectionStyleSettings = sectionData?.styleSettings || {};
  const effectiveStyleSettings = {
    ...styleSettings,  // Parent styles (defaults)
    ...sectionStyleSettings  // Section-specific overrides
  };
  
  // Extract styling values with defaults
  const {
    primaryColor = "#333333",
    secondaryColor = "#a1894c",
    fontFamily = "helvetica, 'helvetica neue', arial, verdana, sans-serif",
    backgroundColor = "#ffffff"
  } = effectiveStyleSettings;
  
  // Save changes to context
  const handleSave = () => {
    const currentSection = sections.find(section => section.id === id) || {};
    updateSection(id, {
      id,
      component_name,
      title: bannerTitle,
      description: bannerDescription,
      image: bannerImage,
      height: bannerHeight,
      overlay: hasOverlay,
      overlay_color: overlayColor,
      overlay_opacity: overlayOpacity,
      text_color: textColor,
      text_position: textPosition,
      styleSettings: {
        ...(currentSection.styleSettings || {}) // Preserve existing styleSettings
      }
    });
    setIsEditing(false);
  };
  // Handle image upload
  const handleImageUpdate = (newImage) => {
    console.log("Header image updated:", newImage);
    setBannerImage(newImage);
  };
  // Helper for safe image URL
  const getSafeImageUrl = (url) => {
    if (!url) return null;
    if (url.includes("&t=")) {
      return url.split("&t=")[0];
    }
    return url;
  };

  // EDIT MODE UI
  if (isEditing && !previewMode) {
    return (
      <div className="email-component email-banner-editor p-3 mb-3 border rounded">
        <h4 className="editor-title mb-3">Edit Banner</h4>
        <div className="mb-3">
          <label className="form-label fw-bold">Banner Title</label>
          <input
            type="text"
            className="form-control"
            value={bannerTitle}
            onChange={(e) => setBannerTitle(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">Banner Description</label>
          <textarea
            className="form-control"
            rows="3"
            value={bannerDescription}
            onChange={(e) => setBannerDescription(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">Banner Image</label>
          <EmailImageUpload
            initialImage={bannerImage}
            onImageUpload={handleImageUpdate}
            className="banner-image-upload"
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">Banner Height</label>
          <input
            type="text"
            className="form-control"
            value={bannerHeight}
            onChange={(e) => setBannerHeight(e.target.value)}
            placeholder="e.g., 300px, 400px, etc."
          />
        </div>
        {/* <div className="mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="overlay-toggle"
              checked={hasOverlay}
              onChange={(e) => setHasOverlay(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="overlay-toggle">
              Add Overlay
            </label>
          </div>
        </div>
        {hasOverlay && (
          <>
            <div className="mb-3">
              <label className="form-label">Overlay Color</label>
              <input
                type="color"
                className="form-control form-control-color"
                value={overlayColor}
                onChange={(e) => setOverlayColor(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Overlay Opacity ({overlayOpacity})</label>
              <input
                type="range"
                className="form-range"
                min="0"
                max="1"
                step="0.1"
                value={overlayOpacity}
                onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
              />
            </div>
          </>
        )}
        <div className="mb-3">
          <label className="form-label">Text Color</label>
          <input
            type="color"
            className="form-control form-control-color"
            value={textColor}
            onChange={(e) => setTextColor(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Text Position</label>
          <select
            className="form-select"
            value={textPosition}
            onChange={(e) => setTextPosition(e.target.value)}
          >
            <option value="below">Below Image</option>
            <option value="overlay">Overlay on Image</option>
            <option value="above">Above Image</option>
          </select>
        </div> */}
        <div className="d-flex justify-content-end mt-3">
          <button
            className="btn btn-outline-secondary me-2"
            onClick={() => setIsEditing(false)}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn bgprimary rounded"
            onClick={handleSave}
            type="button"
          >
            Save Changes
          </button>
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE UI
  return (
    <>
      {!previewMode && (
        <div className="email-component email-banner mb-3">
          <div className="component-controls mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <StyleControls 
              id={id} 
              componentName={component_name}
              styleSettings={styleSettings}
              previewMode={previewMode}
            />
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
              type="button"
            >
              Edit Banner
            </button>
          </div>
        </div>
      )}
      <table
        width="100%"
        cellpadding="0"
        cellspacing="0"
        border="0"
        style={{ fontFamily, backgroundColor }}
      >
        <tr>
          <td style={{ padding: 0, margin: 0, backgroundColor }}>
            <img
              src={bannerImageUrl || "/banner-placeholder.png"}
              alt="Banner"
              style={{
                width: "100%",
                height: bannerHeight,
                display: "block",
                objectFit: "cover",
              }}
            />
          </td>
        </tr>

        {(bannerTitle || bannerDescription) && (
          <tr>
            <td
              align="center"
              style={{
                padding: "24px 16px",
                fontFamily,
                backgroundColor
              }}
            >
              {bannerTitle && (
                <div
                  style={{
                    fontSize: "24px",
                    color: secondaryColor,
                    fontWeight: "600",
                    textAlign: "center",
                    fontFamily,
                    marginBottom: "8px",
                  }}
                >
                  {bannerTitle}
                </div>
              )}
              {bannerDescription && (
                <div
                  style={{
                    fontSize: "16px",
                    color: primaryColor,
                    textAlign: "center",
                    fontFamily,
                  }}
                >
                  {bannerDescription}
                </div>
              )}
            </td>
          </tr>
        )}
      </table>
    </>
  );
};

export default Banner;
